import { useEffect, useState } from "react";

/**
 * Three-state theme model: 'system' follows the OS, 'light' / 'dark'
 * are explicit overrides. Persisted to localStorage so a user's
 * choice survives reloads and (since the storage key is shared) is
 * the same across tabs.
 *
 * Why three states and not just light/dark
 * ----------------------------------------
 * Most operators work in mixed lighting (kitchen morning, boutique
 * evening) and want the app to follow their device. But a few
 * always want dark — auto-following can be disorienting when their
 * OS theme changes for a different reason. 'system' is the default
 * so first-load matches the OS without asking.
 */
export type Theme = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "bunapp.theme";

function readStored(): Theme {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    /* SSR / private mode — fall through to default */
  }
  return "system";
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolve(theme: Theme): ResolvedTheme {
  if (theme === "system") return systemPrefersDark() ? "dark" : "light";
  return theme;
}

/**
 * Apply the resolved theme to <html> by toggling the `dark` class
 * Tailwind keys off. Runs once at module load (before React mounts)
 * so there's no FOUC — the page is correctly themed on first paint.
 */
function applyToDom(resolved: ResolvedTheme) {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", resolved === "dark");
}

// Apply at import-time. main.tsx imports this module before React
// renders, so the <html class="dark"> is already correct when the
// first ReactDOM frame paints.
applyToDom(resolve(readStored()));

/**
 * `theme` is the user's choice (system|light|dark). `resolved` is
 * the actual mode in effect right now (light|dark). Both update
 * when:
 *   - the user calls setTheme()
 *   - localStorage changes from another tab
 *   - the OS theme changes AND theme === 'system'
 */
export function useTheme(): {
  theme: Theme;
  resolved: ResolvedTheme;
  setTheme: (next: Theme) => void;
} {
  const [theme, setThemeState] = useState<Theme>(() => readStored());
  const [resolved, setResolved] = useState<ResolvedTheme>(() =>
    resolve(readStored()),
  );

  // Watch OS-level changes — only matters when theme === 'system'.
  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const r: ResolvedTheme = mq.matches ? "dark" : "light";
      setResolved(r);
      applyToDom(r);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  // Cross-tab sync: another tab writing to localStorage updates us.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const next = readStored();
      setThemeState(next);
      const r = resolve(next);
      setResolved(r);
      applyToDom(r);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setTheme = (next: Theme) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* private mode — non-fatal, the in-memory state still updates */
    }
    setThemeState(next);
    const r = resolve(next);
    setResolved(r);
    applyToDom(r);
  };

  return { theme, resolved, setTheme };
}
